
import React from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

interface ConceptConfirmationProps {
  content: string;
  onConfirm: () => void;
  isLoading: boolean;
  error: string | null;
  onRetry: () => Promise<void>;
  attempts: number;
}

const ConceptConfirmation: React.FC<ConceptConfirmationProps> = ({
  content,
  onConfirm,
  isLoading,
  error,
  onRetry,
  attempts
}) => {
  return (
    <div className="flex items-start mb-6">
      <div className="flex-shrink-0 mr-3">
        <div className="h-8 w-8 rounded-full bg-jade flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
      </div>
      
      <div className="bot-message text-white px-4 py-3 rounded-lg max-w-[85%]">
        <div className="mb-3">
          <h3 className="text-md font-semibold text-jade">Email-Konzept bestätigen</h3>
          <p className="text-sm text-gray-300 mt-1">
            Basierend auf diesem Konzept wird ein HTML-Template erstellt.
          </p>
        </div>
        
        <div className="prose prose-sm dark:prose-invert text-gray-200">
          <div className="concept-content p-3 border border-gray-700 rounded bg-gray-900/50 mb-3">
            {content}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          {error ? (
            <>
              <div className="flex items-center text-red-400 text-sm mb-2 bg-red-900/20 px-3 py-1 rounded-full">
                <AlertCircle className="h-4 w-4 mr-1" />
                <span>{error}</span>
              </div>
              <Button 
                onClick={onRetry} 
                disabled={isLoading}
                variant="outline"
                className="bg-jade/10 border-jade/20 text-jade hover:bg-jade/20"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Bitte warten...
                  </>
                ) : (
                  <>Erneut versuchen</>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={onConfirm} 
                disabled={isLoading}
                variant="outline"
                className="bg-jade/10 border-jade/20 text-jade hover:bg-jade/20"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Konzept bestätigen und Template erstellen
              </Button>
              
              {isLoading && (
                <div className="flex items-center text-jade text-sm ml-2">
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  <span>Generiere Template...</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConceptConfirmation;
